import React from "react";
import styled from "styled-components";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";

export const SessionFetcher = ({
  startDate, setStartDate, endDate, setEndDate,
  minDurationOnly, setMinDurationOnly,
  isFetching, fetchError,
  sessions, selectedSessions, selectedSessionIds, allSelected,
  sessionsExpanded, setSessionsExpanded,
  onFetchSessions, onSelectAll, onToggleSession,
  hideDateInputs = false,
}) => (
  <Card>
    <SectionTitle>1. Fetch Session Data</SectionTitle>
    <Form onSubmit={onFetchSessions}>
      <FormStack>
        {!hideDateInputs && (
          <DateRow>
            <Form.Group style={{ flex: 1 }}>
              <BoldLabel>Start Date</BoldLabel>
              <Form.Control
                required
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <BoldLabel>End Date</BoldLabel>
              <Form.Control
                required
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Group>
          </DateRow>
        )}

        <Form.Group>
          <Form.Check
            type="switch"
            id="min-duration-toggle"
            label="Sessions > 5 min only"
            checked={minDurationOnly}
            onChange={(e) => setMinDurationOnly(e.target.checked)}
          />
        </Form.Group>

        {fetchError && <Alert variant="danger" className="mb-0">{fetchError}</Alert>}

        <FetchButton disabled={isFetching} type="submit">
          {isFetching ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
              Fetching...
            </>
          ) : (
            "Fetch Data"
          )}
        </FetchButton>
      </FormStack>
    </Form>

    {sessions && (
      <SessionListWrapper>
        <SessionListHeader $expanded={sessionsExpanded}>
          <ToggleHeader onClick={() => setSessionsExpanded((prev) => !prev)}>
            <ChevronIcon icon={sessionsExpanded ? faChevronUp : faChevronDown} />
            <h5 style={{ margin: 0 }}>
              Sessions Found: {sessions.length}
              {!sessionsExpanded && selectedSessionIds.length > 0 && (
                <SelectedCount>({selectedSessionIds.length} selected)</SelectedCount>
              )}
            </h5>
          </ToggleHeader>
          {sessions.length > 0 && sessionsExpanded && (
            <Form.Check
              type="checkbox"
              id="select-all"
              label="Select All"
              checked={allSelected}
              onChange={(e) => onSelectAll(e.target.checked)}
            />
          )}
        </SessionListHeader>

        {sessionsExpanded && (
          <>
            {sessions.length === 0 ? (
              <Alert variant="info">No sessions found for this teacher in the selected date range.</Alert>
            ) : (
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th>Date</th>
                    <th>Duration</th>
                    <th>Students</th>
                    <th>Standard</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.session_id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!selectedSessions[session.session_id]}
                          onChange={() => onToggleSession(session.session_id)}
                        />
                      </td>
                      <td>{new Date(session.date).toLocaleString()}</td>
                      <td>{session.duration_minutes?.toFixed(1)} min</td>
                      <td>{session.student_count}</td>
                      <StandardCell>{session.standard || "—"}</StandardCell>
                      <td>
                        <StatusBadge $completed={session.status === "completed"}>
                          {session.status}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </SessionListWrapper>
    )}
  </Card>
);

const Card = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0px 1px 2px 0px rgba(14, 26, 49, 0.05);
  padding: 32px;
`;

const SectionTitle = styled.h4`
  margin-bottom: 24px;
  color: ${colors.nightlyWoods900};
`;

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DateRow = styled.div`
  display: flex;
  gap: 16px;
`;

const BoldLabel = styled(Form.Label)`
  font-weight: bold;
`;

const FetchButton = styled(Button)`
  background-color: ${colors.nightlyWoods500};
  border: none;
  border-radius: 6px;
  font-size: 16px;
  height: 40px;
  padding: 0 16px;
`;

const SessionListWrapper = styled.div`
  margin-top: 24px;
`;

const SessionListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ $expanded }) => ($expanded ? "12px" : "0")};
`;

const ToggleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 12px;
  color: #6c757d;
`;

const SelectedCount = styled.span`
  font-weight: normal;
  font-size: 13px;
  color: #6c757d;
  margin-left: 8px;
`;

const StandardCell = styled.td`
  font-size: 13px;
`;

const StatusBadge = styled.span`
  background-color: ${({ $completed }) => ($completed ? colors.green500 : colors.milkGlass800)};
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
`;
